import React from 'react';
import SearchAndPagination from './SearchAndPagination';

/**
 * CompletedUsersTab
 *
 * Props:
 * - completedUsers: array
 * - expandedUser: string | null
 * - toggleUserAssessments: (email) => void
 * - loadingAssessments: Record<string, boolean>
 * - userAssessments: Record<string, { assessments, total_assessments, error }>
 */
const CompletedUsersTab = ({
  completedUsers,
  expandedUser,
  toggleUserAssessments,
  loadingAssessments,
  userAssessments
}) => {
  const renderUsers = (usersToRender) => {
    if (!usersToRender.length) {
      return (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          <svg
            className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p>No completed sessions found</p>
        </div>
      );
    }

    return usersToRender.map((userData) => {
    const assessmentsData = userAssessments[userData.email];
    const isExpanded = expandedUser === userData.email;
    const isLoading = loadingAssessments[userData.email];
    
    // Get consultant-led count and date - prefer from assessments data if expanded, otherwise use cached count
    let consultantLedCount = 0;
    let lastCompletedDate = null;
    
    if (isExpanded && assessmentsData?.assessments) {
      // Use actual assessments data when expanded
      // When consultant_led_only=true, all returned assessments are consultant-led
      const consultantLedAssessments = assessmentsData.assessments || [];
      consultantLedCount = consultantLedAssessments.length;
      lastCompletedDate = consultantLedAssessments
        .map(a => a.completion_date)
        .filter(d => d)
        .map(d => new Date(d))
        .filter(d => !isNaN(d.getTime()))
        .sort((a, b) => b - a)?.[0];
    } else {
      // Use the accurate count from userData (which was updated by fetchConsultantLedCountsForAllUsers)
      consultantLedCount = userData.completedCount || 0;
      if (userData.lastCompleted) {
        try {
          const date = new Date(userData.lastCompleted);
          if (!isNaN(date.getTime())) {
            lastCompletedDate = date;
          }
        } catch (e) {
          // Invalid date, leave as null
        }
      }
    }

    return (
      <div key={userData.email} className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {userData.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{userData.email}</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {consultantLedCount > 0
                ? `${consultantLedCount} consultant-led ${consultantLedCount === 1 ? 'session' : 'sessions'}`
                : 'No consultant-led sessions'} • Last completed:{' '}
              {lastCompletedDate 
                ? lastCompletedDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })
                : 'N/A'}
            </p>
          </div>
          <button
            onClick={() => toggleUserAssessments(userData.email)}
            className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{isExpanded ? 'Hide' : 'View'} Assessments</span>
          </button>
        </div>

        {isExpanded && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : assessmentsData?.error ? (
              <div className="text-center py-4 text-red-600 dark:text-red-400">
                Error: {assessmentsData.error}
              </div>
            ) : assessmentsData?.assessments?.length ? (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Consultant-Led Assessments ({assessmentsData.assessments.length})
                </h4>
                <div className="grid gap-3">
                  {assessmentsData.assessments.map((assessment) => (
                    <div
                      key={assessment.id}
                      className="flex items-start justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span
                            className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${
                              assessment.assessment_type === 'EMHA'
                                ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                                : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                            }`}
                          >
                            {assessment.assessment_type}
                          </span>
                          {assessment.is_consultant_led && (
                            <span className="text-xs bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded">
                              Consultant-Led
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {assessment.facility_name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          <span className="font-medium">Industry:</span> {assessment.industry_name}
                          {assessment.sub_industry_name && ` • ${assessment.sub_industry_name}`}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Completed:{' '}
                          {new Date(assessment.completion_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                No assessments found for this user
              </div>
            )}
          </div>
        )}
      </div>
    );
    });
  };

  if (!completedUsers.length) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        <svg
          className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <p>No completed sessions found</p>
      </div>
    );
  }

  return (
    <SearchAndPagination
      data={completedUsers}
      searchFields={['name', 'email']}
      itemsPerPage={10}
      searchPlaceholder="Search users by name or email..."
      renderItems={renderUsers}
    />
  );
};

export default CompletedUsersTab;


